var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SparkSchema_1;
import { JSON, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { Column } from './column';
import { SparkDataType } from './sparkDataType';
import './sparkField'; // force import for JSON schema
import { SparkField } from './sparkField';
import { sparkSchemaSchemaValidation } from '../validations.generated';
export const sparkSchemaSchema = {
    $id: 'urn:wisepipe-common-data:SparkSchema',
    type: 'object',
    properties: {
        fields: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-data:SparkField' }
        },
        type: {
            type: 'string'
        }
    },
    required: ['fields', 'type']
};
let SparkSchema = SparkSchema_1 = class SparkSchema {
    constructor(fields) {
        this.fields = fields;
        this.type = 'struct';
        JSON.validate(this, SparkSchema_1);
    }
    // Coversion from other schema types
    static fromJsonSchema(jsonSchema, isDecimalOrg) {
        if (jsonSchema.type !== 'object')
            throw Error(`Cannot convert a JSON schema with type "${jsonSchema.type}" to a SparkSchema. Expected type "object".`);
        const sparkFields = [];
        for (const property in jsonSchema.properties) {
            if (!jsonSchema.properties[property].type) {
                // Sometimes an unexpected empty schema is encountered; treat it as a nullable string
                sparkFields.push(new SparkField(property, SparkDataType.String, true));
            }
            else {
                const schema = jsonSchema.properties[property];
                const sparkType = SparkSchema_1.sparkTypeFromJsonSchemaTypes(schema, isDecimalOrg);
                sparkFields.push(new SparkField(property, sparkType, true));
            }
        }
        const sparkSchema = new SparkSchema_1(sparkFields);
        return sparkSchema;
    }
    static fromColumns(columns) {
        const sparkSchema = new SparkSchema_1(columns.map((column) => {
            return new SparkField(column.name, SparkSchema_1.sparkTypeFromColumnType(column.type), true);
        }));
        return sparkSchema;
    }
    static sparkTypeFromColumnType(columnType) {
        switch (columnType) {
            case 'number':
                return SparkDataType.Double;
            case 'date':
                return SparkDataType.Timestamp;
            case 'string':
                return SparkDataType.String;
            case 'boolean':
                return SparkDataType.Boolean;
            case 'decimal':
                return SparkDataType.Decimal;
            default:
                throw new Error(`Unexpected column type: ${columnType}.`);
        }
    }
    static sparkTypeFromJsonSchemaTypes(jsonSchema, isDecimalOrg) {
        // jsonSchema.type might be a single value like "string" or an array of values like ["null", "string"]
        let jsonSchemaTypes = [].concat(jsonSchema.type);
        jsonSchemaTypes = jsonSchemaTypes.filter((type) => type !== 'null');
        let jsonSchemaType;
        if (jsonSchemaTypes.length > 1)
            jsonSchemaType = this.reduceJsonSchemaTypes(jsonSchemaTypes);
        else
            jsonSchemaType = jsonSchemaTypes[0];
        const jsonSchemaFormat = jsonSchema.format;
        switch (jsonSchemaType) {
            case 'string':
                if (jsonSchemaFormat === 'date-time')
                    return SparkDataType.Timestamp;
                else
                    return SparkDataType.String;
            case 'number':
            case 'integer': {
                if (isDecimalOrg)
                    return SparkDataType.Decimal;
                else
                    return SparkDataType.Double;
            }
            case 'boolean':
                return SparkDataType.Boolean;
            case 'object':
            case 'array':
                // similar to empty schema, for certain schemas that are array, but no object definition property,
                // instead of flattening it into a table during target-s3-csv, we treat it instead as a
                // nullable string as well, see https://github.com/symon-ai/target-s3-csv/pull/19/files#
                console.warn('Detected array or object, this is not a valid spark schema, defaulting to nullable string');
                console.warn(JSON.stringify(jsonSchema, null, 2));
                return SparkDataType.String;
            default:
                throw new Error(`Unexpected JSON schema: ${JSON.stringify(jsonSchema)}.`);
        }
    }
    static reduceJsonSchemaTypes(types) {
        // eg [null, number, string] should be number
        // string should be the most basic type
        if (types.find((type) => type === 'number' || type === 'integer'))
            return 'number';
        else if (types.find((type) => type === 'boolean'))
            return 'boolean';
        else
            return 'string';
    }
    // Conversion to other schema types
    static toColumns(sparkSchema) {
        const sparkFields = sparkSchema.fields;
        const columns = sparkFields.map((field) => {
            return new Column(field.name, SparkSchema_1.toColumnType(field.type));
        });
        return columns;
    }
    static toColumnType(sparkDataType) {
        switch (sparkDataType) {
            case SparkDataType.String:
                return 'string';
            case SparkDataType.Double:
                return 'number';
            case SparkDataType.Boolean:
                return 'boolean';
            case SparkDataType.Timestamp:
                return 'date';
            case SparkDataType.Decimal:
                return 'decimal';
            default:
                throw new Error(`Unsupported Spark data type: ${sparkDataType}.`);
        }
    }
};
SparkSchema = SparkSchema_1 = __decorate([
    JsonSchemaV2(sparkSchemaSchema, sparkSchemaSchemaValidation)
], SparkSchema);
export { SparkSchema };
//# sourceMappingURL=sparkSchema.js.map